import React, { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { api } from '../api';

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const FIPS_TO_CODE = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT","10":"DE",
  "12":"FL","13":"GA","15":"HI","16":"ID","17":"IL","18":"IN","19":"IA","20":"KS",
  "21":"KY","22":"LA","23":"ME","24":"MD","25":"MA","26":"MI","27":"MN","28":"MS",
  "29":"MO","30":"MT","31":"NE","32":"NV","33":"NH","34":"NJ","35":"NM","36":"NY",
  "37":"NC","38":"ND","39":"OH","40":"OK","41":"OR","42":"PA","44":"RI","45":"SC",
  "46":"SD","47":"TN","48":"TX","49":"UT","50":"VT","51":"VA","53":"WA","54":"WV",
  "55":"WI","56":"WY"
};

const STATE_NAMES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',
  IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
  ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
  MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',
  OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
  SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',
  WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
};

export default function Licenses() {
  const [licensed, setLicensed] = useState([]);
  const [tooltip, setTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.me().then(u => { setLicensed(u.state_licenses || []); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  const toggle = async (code) => {
    const updated = licensed.includes(code) ? licensed.filter(s => s !== code) : [...licensed, code];
    setLicensed(updated);
    setSaving(true);
    try { await api.saveSettings({ state_licenses: updated }); } catch (err) { alert(err.message); }
    setSaving(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 980 }}>

      {/* Header */}
      <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 30px rgba(83,74,183,0.08)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 8px #534AB7" }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>State Licenses</span>
            {saving && <span style={{ fontSize: 11, color: "var(--text3)", marginLeft: 4 }}>Saving...</span>}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, background: "rgba(83,74,183,0.08)", border: "0.5px solid rgba(83,74,183,0.2)" }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 4px #534AB7" }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: "#534AB7" }}>{licensed.length} licensed</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, background: "rgba(100,100,100,0.06)", border: "0.5px solid rgba(100,100,100,0.15)" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text3)" }}>{50 - licensed.length} unlicensed</span>
            </div>
          </div>
        </div>
        <div style={{ padding: "8px 20px", fontSize: 11, color: "var(--text3)" }}>
          Click any state to toggle your license. Licensed states glow purple.
        </div>
      </div>

      {/* Map */}
      <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 30px rgba(83,74,183,0.08)", padding: "16px", position: "relative" }}
        onMouseLeave={() => setTooltip(null)}>
        <ComposableMap projection="geoAlbersUsa" style={{ width: "100%", height: "auto" }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map(geo => {
                const code = FIPS_TO_CODE[geo.id];
                if (!code) return null;
                const isLicensed = licensed.includes(code);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => toggle(code)}
                    onMouseMove={e => {
                      setTooltipPos({ x: e.clientX, y: e.clientY });
                      setTooltip(code);
                    }}
                    onMouseLeave={() => setTooltip(null)}
                    style={{
                      default: {
                        fill: isLicensed ? '#534AB7' : 'rgba(83,74,183,0.07)',
                        stroke: isLicensed ? '#7F77DD' : 'rgba(83,74,183,0.35)',
                        strokeWidth: isLicensed ? 1.5 : 0.5,
                        outline: 'none',
                        cursor: 'pointer',
                        filter: isLicensed ? 'drop-shadow(0 0 4px #534AB7)' : 'none',
                        transition: 'fill 0.2s',
                      },
                      hover: {
                        fill: isLicensed ? '#6B62CC' : 'rgba(83,74,183,0.2)',
                        stroke: '#7F77DD',
                        strokeWidth: 1,
                        outline: 'none',
                        cursor: 'pointer',
                      },
                      pressed: {
                        fill: isLicensed ? '#3d35a0' : '#534AB7',
                        outline: 'none',
                      },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>

        {/* Tooltip */}
        {tooltip && (
          <div style={{ position: "fixed", left: tooltipPos.x + 12, top: tooltipPos.y - 36, padding: "6px 12px", borderRadius: 8, background: "rgba(15,12,35,0.95)", border: "0.5px solid rgba(83,74,183,0.5)", fontSize: 12, fontWeight: 600, color: "#fff", pointerEvents: "none", zIndex: 9999, whiteSpace: "nowrap" }}>
            {STATE_NAMES[tooltip]} · {licensed.includes(tooltip) ? '✓ Licensed' : 'Click to license'}
          </div>
        )}
      </div>

      {/* Licensed states list */}
      {licensed.length > 0 && (
        <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden" }}>
          <div style={{ padding: "12px 20px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#534AB7" }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Licensed states</span>
          </div>
          <div style={{ padding: "14px 20px", display: "flex", flexWrap: "wrap", gap: 8 }}>
            {licensed.sort().map(code => (
              <div key={code} onClick={() => toggle(code)}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, background: "rgba(83,74,183,0.08)", border: "0.5px solid rgba(83,74,183,0.3)", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(83,74,183,0.18)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(83,74,183,0.08)"}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#534AB7" }}>{code}</span>
                <span style={{ fontSize: 11, color: "var(--text2)" }}>{STATE_NAMES[code]}</span>
                <span style={{ fontSize: 14, color: "var(--text3)", lineHeight: 1 }}>×</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}